import { FunctionComponent, useState } from 'react'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/modules/context/AuthContext'
import { GET_FAMILY_CONSENT_REQUESTS, GET_OUTGOING_CONSENT_REQUESTS } from '@/modules/API/parent/familyConsentHandler'
import IncomingRequests from './IncomingRequests'
import OutgoingRequests from './OutgoingRequests'
import { FamilyConsentRequest } from '@/types/FamilyConsent'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import Tabs from '@/components/_custom/Tabs/tabs'
import Title from '@/components/_custom/Title/title'
import Loading from '@/components/Loading/Loading'
import styles from './consentPage.module.scss'
import DaycareSelector from '@/components/DaycareSelector/daycareSelector.tsx'

type ConsentPageProps = {
    onClose?: () => void
}

const ConsentPage: FunctionComponent<ConsentPageProps> = ({ onClose }: ConsentPageProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const [tab, setTab] = useState<number>(0)
    const daycareId: number = Number(user?.currentDaycare?.id)

    const {
        data: incomingData,
        loading: incomingLoading,
        refetch: refetchIncoming
    } = useQuery(GET_FAMILY_CONSENT_REQUESTS, {
        variables: { daycareId },
        fetchPolicy: 'network-only'
    })

    const { data: outgoingData, loading: outgoingLoading } = useQuery(GET_OUTGOING_CONSENT_REQUESTS, {
        variables: { daycareId },
        fetchPolicy: 'network-only'
    })

    const incomingRequests: FamilyConsentRequest[] = incomingData?.getFamilyConsentRequests ?? []
    const outgoingRequests: FamilyConsentRequest[] = outgoingData?.getOutgoingConsentRequests ?? []

    return (
        <div className={styles.PagePosition}>
            <div className={styles.Header}>
                <div className={styles.DaycareSelector}>
                    <DaycareSelector />
                </div>
            </div>
            <div className={styles.Content}>
                <Title text={t('consentRequests')} />
                <div className={styles.TabsContainer}>
                    <Tabs
                        value={tab}
                        onChange={(_e, newValue) => setTab(newValue as number)}
                        tabList={[
                            `${t('incomingConsentRequests')} (${incomingRequests.length})`,
                            `${t('outgoingConsentRequests')} (${outgoingRequests.length})`
                        ]}
                    />
                </div>
                {tab === 0 &&
                    (incomingLoading ? (
                        <Loading overall={false} />
                    ) : (
                        <IncomingRequests
                            requests={incomingRequests}
                            daycareId={daycareId}
                            onUpdate={() => void refetchIncoming()}
                        />
                    ))}
                {tab === 1 &&
                    (outgoingLoading ? <Loading overall={false} /> : <OutgoingRequests requests={outgoingRequests} />)}
            </div>
            {onClose && <ButtonTextIcon type='return' onClick={onClose} />}
        </div>
    )
}

export default ConsentPage
